import React, { createContext, useEffect, useState, useCallback } from 'react';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { useAuth } from '@/hooks/useAuth';
import type { ProgressUpdate, ProgressState } from '@/types/progress';

interface SignalRContextType {
  connection: HubConnection | null;
  isConnected: boolean;
  progressState: ProgressState;
  clearProgress: (operation: string) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const SignalRContext = createContext<SignalRContextType | undefined>(undefined);

export function SignalRProvider({ children }: { children: React.ReactNode }) {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [progressState, setProgressState] = useState<ProgressState>({});
  const { getAccessToken, isAuthenticated } = useAuth();

  const clearProgress = useCallback((operation: string) => {
    setProgressState(prev => {
      const newState = { ...prev };
      delete newState[operation];
      return newState;
    });
  }, []);

  useEffect(() => {
    // Clean up existing connection when authentication changes
    if (connection) {
      connection.stop();
      setConnection(null);
      setIsConnected(false);
    }

    if (!isAuthenticated) {
      // Clear progress state when logged out
      setProgressState({});
      return;
    }

    const newConnection = new HubConnectionBuilder()
      .withUrl(`${import.meta.env.VITE_API_BASE_URL.replace('/api', '')}/progressHub`, {
        accessTokenFactory: async () => {
          const token = await getAccessToken();
          return token || '';
        },
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    const startConnection = async () => {
      try {
        await newConnection.start();
        setIsConnected(true);
        console.log('SignalR connected successfully');

        // Listen for progress updates
        newConnection.on('ProgressUpdate', (progressUpdate: ProgressUpdate) => {
          setProgressState(prev => ({
            ...prev,
            [progressUpdate.operation]: progressUpdate,
          }));

          // Auto-clear completed operations after a delay
          if (progressUpdate.isComplete) {
            setTimeout(() => {
              setProgressState(prevState => {
                const newState = { ...prevState };
                delete newState[progressUpdate.operation];
                return newState;
              });
            }, 3000);
          }
        });

      } catch (error) {
        console.error('SignalR connection failed:', error);
        setIsConnected(false);
      }
    };

    newConnection.onclose(() => {
      setIsConnected(false);
      console.log('SignalR connection closed');
    });

    newConnection.onreconnected(() => {
      setIsConnected(true);
      console.log('SignalR reconnected');
    });

    newConnection.onreconnecting(() => {
      setIsConnected(false);
      console.log('SignalR reconnecting...');
    });

    setConnection(newConnection);
    startConnection();

    return () => {
      newConnection?.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]); // Only depend on isAuthenticated to avoid infinite loops

  return (
    <SignalRContext.Provider value={{ connection, isConnected, progressState, clearProgress }}>
      {children}
    </SignalRContext.Provider>
  );
}